import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Select,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";

import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import apiService from "../../service/Api";

interface TableRowData {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  applicant: string;
  contact: string;
  status: string;
  submitted: string;
}

interface SponsorshipResponse {
  id: number;
  event_datetime: string;
  venue_name?: string | null;
  request_type: string;
  status: string;
  event: {
    title: string;
    special_notes: string;
    notes: string;
    event_flyer_image: File | string;
  };
  contact?: {
    id: number;
    first_name: string;
    last_name: string;
    email?: string | null;
    phone?: string | null;
  };
  created_at: string;
}

const getStatusStyles = (status: string) => {
  switch (status.toUpperCase()) {
    case "PENDING":
      return {
        bgcolor: "#F79E1B40",
        color: "#F79E1B",
      };
    case "ACCEPTED":
      return {
        bgcolor: "#4BA66340",
        color: "#4BA663",
      };
    case "MAYBE":
      return {
        bgcolor: "#0000FF26",
        color: "#0000FF",
      };
    case "DECLINED":
      return {
        bgcolor: "#EB433540",
        color: "#EB4335",
      };
    default:
      return {
        bgcolor: "#E5E7EB",
        color: "#6B7280",
      };
  }
};

export default function RequestsDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [tableRows, setTableRows] = useState<TableRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRequests, setTotalRequests] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [declinedCount, setDeclinedCount] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await apiService.get<SponsorshipResponse[]>(
          "/api/bookings/quote-request/",
        );

        const formatted: TableRowData[] = response.data
          .filter((d) => d.request_type === "SPONSORSHIP")
          .map((item) => {
            const dt = new Date(item.event_datetime);

            return {
              id: item.id,
              title: item.event?.title || "Untitled Event",
              date: dt.toLocaleDateString(),
              time: dt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
              location: item.venue_name || "Not specified",
              applicant: item.contact
                ? `${item.contact.first_name} ${item.contact.last_name}`
                : "Unknown",
              contact: item.contact?.phone || "N/A",
              status: item.status || "PENDING",
              submitted: new Date(item.created_at).toLocaleDateString(),
            };
          });

        setTableRows(formatted);
        setTotalRequests(formatted.length);

        setPendingCount(
          formatted.filter((row) => row.status.toLowerCase() === "pending")
            .length,
        );

        setAcceptedCount(
          formatted.filter((row) => row.status.toLowerCase() === "accepted")
            .length,
        );

        setDeclinedCount(
          formatted.filter((row) => row.status.toLowerCase() === "declined")
            .length,
        );
      } catch (err) {
        console.error("Failed to fetch quotes", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  const filteredRows = tableRows
    .filter((row) => {
      const searchMatch =
        row.title.toLowerCase().includes(search.toLowerCase()) ||
        row.applicant.toLowerCase().includes(search.toLowerCase()) ||
        row.location.toLowerCase().includes(search.toLowerCase());

      const statusMatch =
        statusFilter === "all" || row.status.toLowerCase() === statusFilter;

      return searchMatch && statusMatch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.submitted).getTime();
      const dateB = new Date(b.submitted).getTime();

      if (sortOrder === "newest") {
        return dateB - dateA;
      }

      return dateA - dateB;
    });

  if (loading) {
    return <Typography>Loading request...</Typography>;
  }

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        {[
          { label: "Total Requests", value: totalRequests },
          { label: "Pending", value: pendingCount },
          { label: "Accepted", value: acceptedCount },
          { label: "Declined", value: declinedCount },
        ].map((item, i) => (
          <Grid size={{ xs: 12, md: 3 }} key={i}>
            <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>

                <Typography variant="h4" fontWeight="bold">
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: "none" }}>
        <CardContent>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 350 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ color: "text.secondary", borderColor: "#94A3B8", borderRadius: 2 }}
            >
              <MenuItem value="all">All status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
              <MenuItem value="maybe">Maybe</MenuItem>
            </Select>

            <Select
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              sx={{ color: "text.secondary", borderColor: "#94A3B8", borderRadius: 2 }}
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
            </Select>
          </Box>

          <Table>
            <TableHead
              sx={{ backgroundColor: colors.primary[800], p: 0.5, borderRadius: 2 }}
            >
              <TableRow>
                <TableCell>Event Title</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Applicant</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows.map((row, i) => (
                <TableRow
                  key={i}
                  hover
                  onClick={() => navigate(`/sponsorships/view/${row.id}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.time}</TableCell>
                  <TableCell>{row.location}</TableCell>
                  <TableCell>{row.applicant}</TableCell>
                  <TableCell>{row.contact}</TableCell>

                  <TableCell>
                    <Chip
                      label={
                        row.status.charAt(0).toUpperCase() +
                        row.status.slice(1).toLowerCase()
                      }
                      size="small"
                      sx={{
                        ...getStatusStyles(row.status),
                        borderRadius: "8px",
                        minWidth: 90,
                        height: 28,
                        "& .MuiChip-label": {
                          fontWeight: 500,
                          px: 1.5,
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell>{row.submitted}</TableCell>

                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <IconButton>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No requests found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
